import React, { useEffect, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { IRoute, IWaypoint } from '../../models/model-interface';
import useDashboardComputedFunctions from '../../hooks/use-dashboard-computed';
import {
  convertEncodedPolyLineToGoogleMapLatLng,
  generateAnimatedDirectionPolyLine,
  generateStaticDirectionPolyLine,
  getClosestDistanceToPolyline,
} from './helper';
import { useCalculateDirectionMutation } from './queries';
import { CourierVehicleType } from '../../models/model-enum';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import useDashboardStore from '../../hooks/use-dashboard-store';
import { debounce } from 'lodash';
interface componentProps {
  courierId: string | undefined | null;
}

let GLOB_POLYLINES: Array<any> = [];

const debouncedMutationCall = debounce((mutation, variables) => {
  mutation.mutate(variables);
}, 250);

const DashboardCourierDirections = ({ courierId }: componentProps) => {
  const map = useMap();
  const { courierRoute, activeCouriers } = useDashboardStore();
  const { getCourierLocation } = useDashboardComputedFunctions();
  const courierLocation = getCourierLocation(courierId || '') as any;

  const [routeServiceResponse, setRouteServiceResponse] = useState();
  const routeData: IRoute | undefined = courierRoute?.find(
    (route) => route.courier.id === courierId
  );
  const courierData = activeCouriers?.find((courier: any) => {
    return courier.id === courierId;
  });

  const { isResolutionMobile } = useDeviceDetect();

  const calculateDirectionMutation = useCalculateDirectionMutation({
    onSuccess: (data: any) => {
      setRouteServiceResponse(data);
    },
    onError: () => {},
    onSettled: () => {},
  });

  const getNonDeliveredWaypoints = () => {
    const waypoints = routeData?.waypoints || [];
    return waypoints.filter((wp: IWaypoint) => {
      return !wp.isDelivered;
    });
  };

  const getProperTravelMode = (vehicleType: CourierVehicleType) => {
    const travelModesMatchWithCourierVehicleType = {
      [CourierVehicleType.Bike]: 'BICYCLE',
      [CourierVehicleType.EBike]: 'BICYCLE',
      [CourierVehicleType.Scooter]: 'BICYCLE',
      [CourierVehicleType.Car]: 'DRIVE',
    };
    return travelModesMatchWithCourierVehicleType[vehicleType];
  };
  const applyAnimationToDirectionPath = (serviceResponse: any) => {
    if (!serviceResponse.routes) return;
    const { legs = [] } = serviceResponse.routes[0];
    const nonDeliveredWaypoints = getNonDeliveredWaypoints();

    if (!nonDeliveredWaypoints.length) {
      flushPolyLines();
      return;
    }

    const stepDataset = legs.reduce((stepTotal: any, leg: any) => {
      const mergedSteps = [...stepTotal, ...leg.steps];
      return mergedSteps;
    }, []);

    const { closestDistanceIndex: waypointIntersectionStepIndex } =
      getClosestDistanceToPolyline(stepDataset, nonDeliveredWaypoints[0]);

    const polyLines = stepDataset.map((step: any, index: number) => {
      const convertedPolyLineDataset = convertEncodedPolyLineToGoogleMapLatLng(
        step.polyline.encodedPolyline
      );
      const generationFunction =
        index < waypointIntersectionStepIndex ||
        nonDeliveredWaypoints.length === 1
          ? generateAnimatedDirectionPolyLine
          : generateStaticDirectionPolyLine;
      return generationFunction(map, convertedPolyLineDataset);
    });

    flushPolyLines();
    GLOB_POLYLINES = polyLines;
  };

  const renderRouteServiceData = (routeServiceData: any) => {
    applyAnimationToDirectionPath(routeServiceData);
  };

  const drawActiveCourierPath = () => {
    const nonDeliveredWaypoints = getNonDeliveredWaypoints();

    if (!nonDeliveredWaypoints.length) {
      flushPolyLines();
      return;
    }
    debouncedMutationCall(calculateDirectionMutation, {
      origin: courierLocation,
      destination: nonDeliveredWaypoints[nonDeliveredWaypoints.length - 1],
      waypoints: nonDeliveredWaypoints,
      travelMode: getProperTravelMode(
        courierData?.vehicleType as CourierVehicleType
      ),
    });
  };

  const flushPolyLines = () => {
    for (let a = 0; a <= GLOB_POLYLINES.length - 1; a++) {
      const polyLine = GLOB_POLYLINES[a];
      if (polyLine.$clearTimer) {
        polyLine.$clearTimer();
      }
      polyLine.setMap(null);
    }
    GLOB_POLYLINES = [];
  };

  const panMapToMapElements = () => {
    if (!courierId || !courierLocation) {
      return;
    }
    const nonDeliveredWaypoints = getNonDeliveredWaypoints();
    const BOUND_LEFT_PADDING = isResolutionMobile ? 0 : 350;
    const BOUND_TOP_PADDING = isResolutionMobile ? 0 : 56;
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(
      new google.maps.LatLng(
        courierLocation.latitude,
        courierLocation.longitude
      )
    );

    nonDeliveredWaypoints.forEach((waypoint: IWaypoint) => {
      bounds.extend(
        new google.maps.LatLng(
          waypoint.location.latitude,
          waypoint.location.longitude
        )
      );
    });
    if (nonDeliveredWaypoints.length !== 0) {
      map?.fitBounds(bounds, {
        left: BOUND_LEFT_PADDING,
        top: BOUND_TOP_PADDING,
      });
    } else {
      map?.panTo(
        new google.maps.LatLng(
          courierLocation.latitude,
          courierLocation.longitude
        )
      );
    }
  };

  useEffect(() => {
    if (!courierId) {
      flushPolyLines();
    }
  }, [courierId]);

  useEffect(() => {
    if (routeServiceResponse) {
      renderRouteServiceData(routeServiceResponse);
    }
  }, [routeServiceResponse]);

  useEffect(() => {
    panMapToMapElements();
  }, [courierId, courierLocation, courierRoute, activeCouriers]);

  useEffect(() => {
    drawActiveCourierPath();
  }, [courierId, courierLocation, courierRoute, activeCouriers]);

  return <></>;
};
export default DashboardCourierDirections;
